"use client";

import { ReactNode, useState } from "react";

// eslint-disable-next-line import/named
import { FieldValues, useForm } from "react-hook-form";
import { useImmer } from "use-immer";
import { model } from "./model";
import { preSubmit } from "@/forms/validation";

import { clientPost } from "@/service/client-api/client-api";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextareaField } from "@/forms/fields/textarea-field/textarea-field";
import { Form } from "@/components/ui/form";
import { CheckIcon, LoaderIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";
import { handleUserError } from "@/service/client-api/error-handler";

export function ReportComplaint({
  report,
  children,
  endpoint,
}: {
  report: MedicalFacilityUnitReport | MedicalFacilityReport;
  children: ReactNode;
  endpoint: string;
}) {
  const [fields, setFields] = useImmer(model);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });

  const onSubmit = async ({ complaint }: FieldValues) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    try {
      await clientPost(
        endpoint,
        {},
        {
          reportId: report.id,
          complaint,
        },
      );

      setSuccess(true);
      setLoading(false);

      setFields(() => model);
      form.reset({ message: "" });
    } catch (caughtError) {
      setLoading(false);
      handleUserError(caughtError, setError, setFields, setSuccess);
    }
  };

  return (
    <>
      <div onClick={() => setIsOpen(true)}>{children}</div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent innerClassName={"px-6 py-10"}>
          {!success ? (
            <>
              <h3 className={"text-md mb-4 font-semibold"}>
                Dlaczego chcesz zgłosić tę opinię?
              </h3>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <TextareaField field={fields.complaint} />

                  <Button
                    type={"submit"}
                    className={"flex"}
                    variant={"blue"}
                    disabled={loading}
                  >
                    {loading && <LoaderIcon className="animate-spin" />}
                    Zgłoś
                  </Button>

                  <FormError error={error} />
                </form>
              </Form>
            </>
          ) : (
            <div className={"mx-auto w-[16rem] text-center"}>
              <div className={"mb-4 flex justify-center"}>
                <CheckIcon size={"24px"} />
              </div>
              <h3 className={"mb-2 font-semibold"}>Dziękujemy za reakcję!</h3>
              <p className={"text-sm"}>
                Dziękujemy za zgłoszenie. Postaramy się jak najszybciej
                odpowiedzieć.
              </p>
            </div>
          )}
          <DialogClose />
        </DialogContent>
      </Dialog>
    </>
  );
}
